import Template from "../../components/Template";
import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx-js-style";
import { useAuth } from '../../context/AuthContext';
import { formatDateWithoutHour } from "../../utils/date";
import { formatCurrencyFromDb, maskCpfCnpj } from "../../utils/masks";
import logoEmpresa from "../../components/logoEmpresaBase64";
import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
import usePermission from "../../hooks/usePermission";
import ModalVincularNotaBoleto from "./ModalVincularNotaBoleto";

const API = '/relatorios/boletos';

const FILTROS_INICIAIS = {
    obra: "",
    cliente: "",
    beneficiario: "",
    situacao: "",
    dataVencimentoInicio: "",
    dataVencimentoFim: "",
    dataEmissaoInicio: "",
    dataEmissaoFim: "",
    dataPagamentoInicio: "",
    dataPagamentoFim: "",
};

const RESUMO_ZERADO = { quantidade: 0, valorTotal: 0, semObraQuantidade: 0, semObraValor: 0 };

const SITUACOES = [
    { value: "", label: "Todos (exceto cancelados)" },
    { value: "a-pagar", label: "A pagar (a vencer + vencidos)" },
    { value: "a-vencer", label: "A vencer" },
    { value: "vencidos", label: "Vencidos" },
    { value: "pagos", label: "Pagos" },
    { value: "baixados", label: "Baixados" },
    { value: "cancelados", label: "Cancelados" },
    { value: "todos", label: "Todos (inclusive cancelados)" },
];

const PERIODOS = [
    ['dataVencimento', 'vencimento'],
    ['dataEmissao', 'emissão'],
    ['dataPagamento', 'pagamento'],
];

// Mesma leitura de status da tela de Boletos: "Vence Hoje" e "Vencido" saem da
// data, porque a marcação de vencido no banco só roda uma vez por dia.
const STATUS_FINALIZADOS = ['paid', 'closed', 'canceled'];
const LABELS_STATUS = { open: 'Em Aberto', paid: 'Pago', overdue: 'Vencido', closed: 'Baixado', canceled: 'Cancelado' };
const labelStatus = (status, dataVencimento, hoje) => {
    const vencimento = dataVencimento ? String(dataVencimento).split('T')[0] : null;
    if (!STATUS_FINALIZADOS.includes(status) && vencimento && hoje) {
        if (vencimento === hoje) return 'Vence Hoje';
        if (vencimento < hoje) return 'Vencido';
    }
    return LABELS_STATUS[status] || status || '';
};

const nomeObra = (row) => row.obra?.nomeProjeto?.trim().toUpperCase() || 'SEM OBRA VINCULADA';

// Linha digitável no formato impresso no boleto bancário (47 dígitos):
// AAAAA.AAAAA BBBBB.BBBBBB CCCCC.CCCCCC D EEEEEEEEEEEEEE. Outro tamanho
// (convênio/concessionária) sai como veio.
const formatarLinhaDigitavel = (linha) => {
    const d = String(linha || '').replace(/\D/g, '');
    if (d.length !== 47) return d;
    return `${d.slice(0, 5)}.${d.slice(5, 10)} ${d.slice(10, 15)}.${d.slice(15, 21)} ${d.slice(21, 26)}.${d.slice(26, 32)} ${d.slice(32, 33)} ${d.slice(33)}`;
};

// Coluna que só existe nas exportações: na tela ocuparia espaço sem uso,
// mas no Excel/PDF é o que se digita no app do banco para pagar.
const COLUNA_LINHA_DIGITAVEL = {
    id: 'linhaDigitavel',
    label: 'Linha digitável',
    visible: true,
    somenteExportacao: true,
    formatter: (value) => formatarLinhaDigitavel(value),
};

// Data do banco (@db.Date, meia-noite UTC) como número de série do Excel, para
// a planilha ordenar e filtrar por data de verdade.
const dataParaSerialExcel = (valor) => {
    if (!valor) return '';
    const [ano, mes, dia] = String(valor).split('T')[0].split('-').map(Number);
    if (!ano || !mes || !dia) return '';
    return (Date.UTC(ano, mes - 1, dia) - Date.UTC(1899, 11, 30)) / 86400000;
};

const RelatorioBoletos = () => {
    const { auth } = useAuth();
    const [lista, setLista] = useState([]);
    const [obras, setObras] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [columns, setColumns] = useState([]);
    const [menuOpen, setMenuOpen] = useState(null);
    const [showAlertDialog, setShowAlertDialog] = useState(false);
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [exportando, setExportando] = useState(null);
    const [filters, setFilters] = useState(FILTROS_INICIAIS);
    const [erroFiltro, setErroFiltro] = useState("");
    const [resumo, setResumo] = useState(RESUMO_ZERADO);
    const [hoje, setHoje] = useState("");
    // Boleto sem obra cuja nota o usuário está escolhendo (Vincular nota)
    const [boletoVinculo, setBoletoVinculo] = useState(null);
    const podeVincular = usePermission('ATUALIZAR_BOLETO');

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filtros efetivamente aplicados: paginar e exportar usam estes, e não o que
    // está digitado no painel e ainda não foi confirmado com "Filtrar".
    const appliedFiltersRef = useRef(FILTROS_INICIAIS);

    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);

    // Colunas pedidas no chamado (Obra, Valor, Beneficiário, Vencimento) vêm
    // visíveis e nessa ordem; as demais ficam disponíveis no seletor.
    useEffect(() => {
        setColumns([
            { id: 'obra', label: 'Obra', visible: true, formatter: (value, row) => nomeObra(row) },
            // Local da obra, vindo do cadastro da obra
            { id: 'uf', label: 'Estado', visible: true, formatter: (value) => value || '' },
            { id: 'municipio', label: 'Município', visible: true, formatter: (value) => value?.toUpperCase() || '' },
            { id: 'valor', label: 'Valor', visible: true, monetario: true, formatter: (value) => formatCurrencyFromDb(value) },
            { id: 'beneficiario', label: 'Beneficiário', visible: true, formatter: (value) => value?.toUpperCase() || '' },
            { id: 'dataVencimento', label: 'Vencimento', visible: true, data: true, formatter: (value) => value ? formatDateWithoutHour(value) : '' },
            { id: 'status', label: 'Situação', visible: false, formatter: (value, row) => labelStatus(value, row.dataVencimento, hoje) },
            { id: 'cliente', label: 'Cliente', visible: false, formatter: (value) => value?.toUpperCase() || '' },
            { id: 'beneficiarioDocumento', label: 'CNPJ/CPF Beneficiário', visible: false, formatter: (value) => value ? maskCpfCnpj(value) : '' },
            { id: 'notaFiscal', label: 'Nota Fiscal', visible: false, formatter: (value) => value ? `${value.numero} (${value.tipo})` : '' },
            { id: 'dataEmissao', label: 'Emissão', visible: false, data: true, formatter: (value) => value ? formatDateWithoutHour(value) : '' },
            { id: 'dataPagamento', label: 'Pagamento', visible: false, data: true, formatter: (value) => value ? formatDateWithoutHour(value) : '' },
        ]);
    }, [hoje]);

    useEffect(() => {
        const fetchInitialData = async () => {
            // Clientes é opcional: quem não enxerga o cadastro de clientes
            // continua usando o relatório, só sem esse filtro.
            const [obrasRes, clientesRes] = await Promise.all([
                api.get("/obras/listar/todas").catch(() => null),
                api.get("/clientes/todos").catch(() => null),
            ]);
            if (!obrasRes) {
                setMessage("Erro ao carregar a lista de obras para o filtro.");
                setShowAlertDialog(true);
            }
            // trim: há obra cadastrada com espaços no início, que iria para o topo da lista
            const ordenarPor = (campo) => (a, b) => String(a[campo] || '').trim().localeCompare(String(b[campo] || '').trim(), 'pt-BR');
            setObras([...(obrasRes?.data || [])].sort(ordenarPor('nomeProjeto')));
            const listaClientes = Array.isArray(clientesRes?.data) ? clientesRes.data : (clientesRes?.data?.records || []);
            setClientes([...listaClientes].sort(ordenarPor('nome')));
        };
        fetchInitialData();
    }, []);

    const montarParams = (f) => {
        const params = new URLSearchParams();
        if (f.obra) params.append('obraId', f.obra);
        if (f.cliente) params.append('clienteId', f.cliente);
        if (f.beneficiario.trim()) params.append('beneficiario', f.beneficiario.trim());
        if (f.situacao) params.append('situacao', f.situacao);
        PERIODOS.forEach(([campo]) => {
            if (f[`${campo}Inicio`]) params.append(`${campo}Inicio`, f[`${campo}Inicio`]);
            if (f[`${campo}Fim`]) params.append(`${campo}Fim`, f[`${campo}Fim`]);
        });
        return params;
    };

    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchBoletos = async (page, size, currentFilters) => {
        setIsLoading(true);
        const idRequisicao = requisicaoMaisRecente.iniciar();
        try {
            const params = montarParams(currentFilters);
            params.append('page', page);
            params.append('pageSize', size);

            const response = await api.get(`${API}?${params.toString()}`);
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            setLista(response.data.records || []);
            setTotalRecords(response.data.total || 0);
            setTotalPages(response.data.totalPages || 0);
            setCurrentPage(response.data.page || 1);
            setResumo(response.data.resumo || RESUMO_ZERADO);
            setHoje(response.data.hoje || "");
        } catch (error) {
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;
            console.error("Erro ao buscar boletos:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar boletos.");
            setShowAlertDialog(true);
            setLista([]);
            setTotalRecords(0);
            setTotalPages(0);
            setResumo(RESUMO_ZERADO);
        } finally {
            if (requisicaoMaisRecente.ehAtual(idRequisicao)) setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBoletos(1, pageSize, appliedFiltersRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validarFiltros = (f) => {
        for (const [campo, rotulo] of PERIODOS) {
            const inicio = f[`${campo}Inicio`];
            const fim = f[`${campo}Fim`];
            if (inicio && fim && inicio > fim) {
                return `A data inicial de ${rotulo} é maior que a final.`;
            }
        }
        return "";
    };

    const applyFilters = () => {
        const erro = validarFiltros(filters);
        setErroFiltro(erro);
        if (erro) return;
        appliedFiltersRef.current = filters;
        fetchBoletos(1, pageSize, filters);
        setMenuOpen(null);
    };

    const clearFilters = () => {
        setFilters(FILTROS_INICIAIS);
        setErroFiltro("");
        appliedFiltersRef.current = FILTROS_INICIAIS;
        fetchBoletos(1, pageSize, FILTROS_INICIAIS);
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages && page !== currentPage) {
            fetchBoletos(page, pageSize, appliedFiltersRef.current);
        }
    };

    const handlePageSizeChange = (newPageSize) => {
        const firstRecordOnPage = (currentPage - 1) * pageSize + 1;
        const newTotalPages = Math.ceil(totalRecords / newPageSize);
        const targetPage = Math.min(Math.ceil(firstRecordOnPage / newPageSize), newTotalPages) || 1;
        setPageSize(newPageSize);
        fetchBoletos(targetPage, newPageSize, appliedFiltersRef.current);
    };

    const toggleColumnVisibility = (id) => {
        setColumns((prev) => prev.map((col) => col.id === id ? { ...col, visible: !col.visible } : col));
    };

    // Descreve os filtros aplicados, para o cabeçalho do PDF/Excel
    const descreverFiltros = () => {
        const f = appliedFiltersRef.current;
        const linhas = [];
        if (f.obra) {
            linhas.push(`Obra: ${f.obra === 'sem-obra' ? 'SEM OBRA VINCULADA' : (obras.find(o => String(o.id) === String(f.obra))?.nomeProjeto?.toUpperCase() || f.obra)}`);
        }
        if (f.cliente) {
            linhas.push(`Cliente: ${clientes.find(c => String(c.id) === String(f.cliente))?.nome?.toUpperCase() || f.cliente}`);
        }
        if (f.beneficiario.trim()) linhas.push(`Beneficiário: ${f.beneficiario.trim()}`);
        linhas.push(`Situação: ${SITUACOES.find(s => s.value === f.situacao)?.label || 'Todos'}`);
        PERIODOS.forEach(([campo, rotulo]) => {
            const inicio = f[`${campo}Inicio`];
            const fim = f[`${campo}Fim`];
            if (inicio || fim) {
                linhas.push(`Período de ${rotulo}: ${inicio ? formatDateWithoutHour(inicio) : '...'} a ${fim ? formatDateWithoutHour(fim) : '...'}`);
            }
        });
        return linhas;
    };

    const exportarDados = async (tipo) => {
        if (!totalRecords) {
            setMessage('Não há dados para exportar!');
            setShowAlertDialog(true);
            return;
        }
        setExportando(tipo);
        try {
            const params = montarParams(appliedFiltersRef.current);
            params.append('exportAll', 'true');
            const response = await api.get(`${API}?${params.toString()}`);
            const dados = response.data.records || [];
            if (dados.length === 0) {
                setMessage('Não há dados para exportar!');
                setShowAlertDialog(true);
                return;
            }
            if (tipo === 'excel') exportarExcel(dados, response.data.hoje);
            else exportarPDF(dados);
        } catch (error) {
            console.error("Erro ao exportar dados:", error);
            setMessage(error.response?.data?.error || "Falha ao preparar dados para exportação.");
            setShowAlertDialog(true);
        } finally {
            setExportando(null);
        }
    };

    const exportarPDF = (data) => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(16);
        doc.text("Relatório de Boletos", 14, 20);
        doc.setFontSize(10);

        let yPos = 28;
        descreverFiltros().forEach((linha) => {
            doc.text(linha, 14, yPos);
            yPos += 6;
        });

        const visibleColumns = [...columns.filter(c => c.visible), COLUNA_LINHA_DIGITAVEL];
        const head = [visibleColumns.map(c => c.label)];
        const body = data.map(row => visibleColumns.map(col => col.formatter(row[col.id], row)));

        const total = data.reduce((sum, item) => sum + (Number(item.valor) || 0), 0);
        const indiceValor = visibleColumns.findIndex(c => c.monetario);
        if (indiceValor >= 0) {
            const totalRow = Array(visibleColumns.length).fill('');
            if (indiceValor > 0) totalRow[indiceValor - 1] = { content: 'TOTAL:', styles: { fontStyle: 'bold', halign: 'right' } };
            totalRow[indiceValor] = { content: formatCurrencyFromDb(total), styles: { fontStyle: 'bold' } };
            body.push(totalRow);
        }

        const columnStyles = visibleColumns.reduce((acc, col, idx) => {
            acc[idx] = {
                overflow: 'linebreak',
                ...(col.monetario ? { halign: 'right', cellWidth: 30 } : {}),
                ...(col.data ? { cellWidth: 24 } : {}),
                // 54 caracteres em fonte menor cabem em duas linhas sem quebrar os blocos
                ...(col.somenteExportacao ? { cellWidth: 46, fontSize: 7 } : {}),
            };
            return acc;
        }, {});

        autoTable(doc, {
            startY: yPos + 2,
            head,
            body,
            margin: { left: 6, right: 6, bottom: 22 },
            headStyles: { fillColor: [30, 120, 180], fontSize: 9, halign: 'left' },
            bodyStyles: { valign: 'middle' },
            styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
            columnStyles,
        });

        const pageCount = doc.internal.getNumberOfPages();
        const dataEmissao = new Date().toLocaleString('pt-BR');
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100);
            doc.text(`Emitido por: ${auth.user.name || auth.user.nome || "Sistema"}`, 14, doc.internal.pageSize.height - 15);
            doc.text(`Data/Hora: ${dataEmissao}`, 14, doc.internal.pageSize.height - 10);
            doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
            if (logoEmpresa) {
                const logoWidth = 40, logoHeight = 12, pageWidth = doc.internal.pageSize.getWidth();
                doc.addImage(logoEmpresa, 'PNG', (pageWidth - logoWidth) / 2, doc.internal.pageSize.height - 18, logoWidth, logoHeight);
            }
        }

        doc.save("relatorio_boletos.pdf");
    };

    const exportarExcel = (data, hojeExportacao) => {
        const borderThin = { style: 'thin', color: { rgb: 'B0B0B0' } };
        const borders = { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin };
        const titleStyle = {
            font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: borders,
        };
        const filterStyle = {
            font: { italic: true, sz: 11, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: 'DCE6F1' } },
            alignment: { horizontal: 'left', vertical: 'center' },
            border: borders,
        };
        const headerStyle = {
            font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '2E75B6' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: borders,
        };
        const cellStyle = (par) => ({
            font: { sz: 10, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: par ? 'F2F7FB' : 'FFFFFF' } },
            alignment: { vertical: 'center' },
            border: borders,
        });
        const totalStyle = {
            font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { horizontal: 'right', vertical: 'center' },
            border: borders,
        };

        const visibleColumns = [...columns.filter(c => c.visible), COLUNA_LINHA_DIGITAVEL];
        const colCount = visibleColumns.length;
        const filterLines = descreverFiltros();

        const wsData = [];
        const merges = [];

        const titleRow = Array(colCount).fill('');
        titleRow[0] = 'Relatório de Boletos';
        wsData.push(titleRow);
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });

        filterLines.forEach((line) => {
            const filterRow = Array(colCount).fill('');
            filterRow[0] = line;
            wsData.push(filterRow);
            merges.push({ s: { r: wsData.length - 1, c: 0 }, e: { r: wsData.length - 1, c: colCount - 1 } });
        });

        wsData.push(Array(colCount).fill(''));

        const headerRowIndex = wsData.length;
        wsData.push(visibleColumns.map(c => c.label));

        const dataStartRow = wsData.length;
        data.forEach(row => {
            wsData.push(visibleColumns.map(col => {
                if (col.monetario) return Number(row[col.id]) || 0;
                if (col.data) return dataParaSerialExcel(row[col.id]);
                if (col.id === 'status') return labelStatus(row.status, row.dataVencimento, hojeExportacao || hoje);
                return col.formatter(row[col.id], row);
            }));
        });
        const dataEndRow = wsData.length - 1;

        const indiceValor = visibleColumns.findIndex(c => c.monetario);
        const totalRowIndex = wsData.length;
        if (indiceValor >= 0) {
            const totalRow = Array(colCount).fill('');
            if (indiceValor > 0) totalRow[indiceValor - 1] = 'TOTAL:';
            totalRow[indiceValor] = Math.round(data.reduce((sum, item) => sum + (Number(item.valor) || 0), 0) * 100) / 100;
            wsData.push(totalRow);
        }

        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        worksheet['!merges'] = merges;

        const estilizar = (r, c, estilo) => {
            const ref = XLSX.utils.encode_cell({ r, c });
            if (!worksheet[ref]) worksheet[ref] = { v: '', t: 's' };
            worksheet[ref].s = estilo;
            return worksheet[ref];
        };

        for (let c = 0; c < colCount; c++) estilizar(0, c, titleStyle);
        for (let r = 1; r <= filterLines.length; r++) {
            for (let c = 0; c < colCount; c++) estilizar(r, c, filterStyle);
        }
        for (let c = 0; c < colCount; c++) estilizar(headerRowIndex, c, headerStyle);

        for (let r = dataStartRow; r <= dataEndRow; r++) {
            const base = cellStyle((r - dataStartRow) % 2 === 0);
            visibleColumns.forEach((col, c) => {
                if (col.monetario) {
                    estilizar(r, c, { ...base, alignment: { ...base.alignment, horizontal: 'right' }, numFmt: 'R$ #,##0.00' });
                } else if (col.data) {
                    const cell = estilizar(r, c, { ...base, alignment: { ...base.alignment, horizontal: 'center' }, numFmt: 'dd/mm/yyyy' });
                    if (cell.v !== '') cell.t = 'n';
                } else {
                    estilizar(r, c, base);
                }
            });
        }

        if (indiceValor >= 0) {
            for (let c = 0; c < colCount; c++) {
                estilizar(totalRowIndex, c, c === indiceValor ? { ...totalStyle, numFmt: 'R$ #,##0.00' } : totalStyle);
            }
        }

        // Autofiltro no cabeçalho: o fluxo de caixa costuma ser recortado por
        // obra ou por dia direto na planilha.
        if (dataEndRow >= dataStartRow) {
            worksheet['!autofilter'] = {
                ref: XLSX.utils.encode_range({ s: { r: headerRowIndex, c: 0 }, e: { r: dataEndRow, c: colCount - 1 } })
            };
        }

        worksheet['!cols'] = visibleColumns.map((col) => {
            if (col.monetario) return { wch: 18 };
            if (col.data) return { wch: 13 };
            let maxLen = col.label.length;
            data.forEach(row => {
                const val = String(col.formatter(row[col.id], row) || '');
                if (val.length > maxLen) maxLen = val.length;
            });
            return { wch: Math.min(Math.max(maxLen + 2, 12), 60) };
        });
        worksheet['!rows'] = [{ hpt: 30 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Boletos");
        XLSX.writeFile(workbook, "Relatorio_Boletos.xlsx");
    };

    const handleClickOutside = (event) => {
        if (buttonRef.current && !buttonRef.current.contains(event.target) && buttonFiltrarRef.current && !buttonFiltrarRef.current.contains(event.target)) {
            setMenuOpen(null);
        }
    };
    useEffect(() => { document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside); }, []);

    const toggleMenu = (menu) => setMenuOpen(menuOpen === menu ? null : menu);
    const fecharComEsc = (e) => { if (e.key === 'Escape') setMenuOpen(null); };

    // No celular o painel ocupa a largura da tela; ancorado no botão ele saía
    // pela esquerda e cortava os campos.
    const classePainel = "fixed inset-x-4 top-20 md:absolute md:inset-x-auto md:top-full md:right-0 md:mt-2 bg-white rounded-md shadow-lg z-50 p-4 border";

    const campoData = (campo, rotulo) => (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={campo} className="text-slate-700 text-sm font-medium">{rotulo}</label>
            <input
                type="date"
                id={campo}
                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                value={filters[campo]}
                onChange={(e) => setFilters({ ...filters, [campo]: e.target.value })}
            />
        </div>
    );

    const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const pageWindowSize = 5;
    const startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    const visiveis = columns.filter(col => col.visible);
    const indiceValorTabela = visiveis.findIndex(c => c.monetario);
    const totalPagina = lista.reduce((sum, item) => sum + (Number(item.valor) || 0), 0);
    const filtrosAtivos = Object.entries(appliedFiltersRef.current).filter(([, v]) => String(v).trim() !== '').length;

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div>
                    <h1 className="text-stone-800 text-xl md:text-2xl font-medium">Relatório de Boletos</h1>
                    <p className="text-sm text-stone-600 mt-1">
                        Boletos por vencimento para o fluxo de caixa. A obra vem da nota fiscal vinculada ao boleto.
                    </p>
                </div>

                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <label htmlFor="pageSize" className="text-stone-600 text-sm font-medium">Ver</label>
                        <select id="pageSize" className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm" value={pageSize} onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                        </select>
                        <span className="text-stone-600 text-sm font-medium">Resultados</span>
                    </div>

                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative flex items-center" onKeyDown={fecharComEsc}>
                            <button type="button" className="justify-end items-center gap-1 flex" aria-expanded={menuOpen === "colunas"} onClick={() => toggleMenu("colunas")}>
                                <span className="justify-start items-center gap-2 flex"><img className="w-6 h-6" src="/images/icon/columns.png" alt="" /><span className="text-stone-600 text-sm font-medium">Colunas</span></span>
                                <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="" />
                            </button>
                            {menuOpen === "colunas" && (
                                <div className={`${classePainel} md:w-60 flex flex-col gap-3`}>
                                    {columns.map((col) => (
                                        <div key={col.id} className="flex items-center gap-2">
                                            <input type="checkbox" id={`col-${col.id}`} checked={col.visible} onChange={() => toggleColumnVisibility(col.id)} />
                                            <label htmlFor={`col-${col.id}`} className="text-sm text-gray-700">{col.label}</label>
                                        </div>
                                    ))}
                                    <p className="text-xs text-slate-500">O PDF e o Excel saem com as colunas marcadas.</p>
                                </div>
                            )}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative flex items-center" onKeyDown={fecharComEsc}>
                            <button type="button" className="justify-start items-center gap-1 flex" aria-expanded={menuOpen === "filtro"} onClick={() => toggleMenu("filtro")}>
                                <span className="justify-start items-center gap-2 flex">
                                    <img className="w-8 h-8" src="/images/icon/filter.png" alt="" />
                                    <span className="text-stone-600 text-sm font-medium">Filtrar{filtrosAtivos > 0 ? ` (${filtrosAtivos})` : ''}</span>
                                </span>
                                <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="" />
                            </button>
                            {menuOpen === "filtro" && (
                                <div role="dialog" aria-label="Filtrar Boletos" className={`${classePainel} md:w-[440px] max-h-[75vh] overflow-y-auto`}>
                                    <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); applyFilters(); }}>
                                        <div className="text-stone-800 text-xl font-medium">Filtrar Boletos</div>

                                        <div className="grid grid-cols-2 gap-4">
                                            {campoData('dataVencimentoInicio', 'Vencimento Inicial')}
                                            {campoData('dataVencimentoFim', 'Vencimento Final')}
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label htmlFor="situacao" className="text-slate-700 text-sm font-medium">Situação</label>
                                            <select id="situacao" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.situacao} onChange={(e) => setFilters({ ...filters, situacao: e.target.value })}>
                                                {SITUACOES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label htmlFor="obra" className="text-slate-700 text-sm font-medium">Obra</label>
                                            <select id="obra" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.obra} onChange={(e) => setFilters({ ...filters, obra: e.target.value })}>
                                                <option value="">Todas as obras</option>
                                                <option value="sem-obra">Sem obra vinculada</option>
                                                {obras.map((o) => (<option key={o.id} value={o.id}>{o.nomeProjeto?.toUpperCase()}</option>))}
                                            </select>
                                        </div>

                                        {clientes.length > 0 && (
                                            <div className="flex flex-col gap-1.5">
                                                <label htmlFor="cliente" className="text-slate-700 text-sm font-medium">Cliente da obra</label>
                                                <select id="cliente" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" value={filters.cliente} onChange={(e) => setFilters({ ...filters, cliente: e.target.value })}>
                                                    <option value="">Todos os clientes</option>
                                                    {clientes.map((c) => (<option key={c.id} value={c.id}>{c.nome?.toUpperCase()}</option>))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="flex flex-col gap-1.5">
                                            <label htmlFor="beneficiario" className="text-slate-700 text-sm font-medium">Beneficiário (nome ou CNPJ)</label>
                                            <input id="beneficiario" type="text" className="w-full px-3 py-2 bg-white rounded-md border border-slate-300" placeholder="Ex.: Tudo Ferro ou 12.345.678" value={filters.beneficiario} onChange={(e) => setFilters({ ...filters, beneficiario: e.target.value })} />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            {campoData('dataEmissaoInicio', 'Emissão Inicial')}
                                            {campoData('dataEmissaoFim', 'Emissão Final')}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            {campoData('dataPagamentoInicio', 'Pagamento Inicial')}
                                            {campoData('dataPagamentoFim', 'Pagamento Final')}
                                        </div>

                                        {erroFiltro && <span id="erro-filtro" role="alert" className="text-red-600 text-sm">{erroFiltro}</span>}

                                        <div className="flex items-center justify-between gap-4 mt-2">
                                            <button type="button" className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex items-center justify-center gap-2 disabled:opacity-60" disabled={isLoading} onClick={clearFilters}><img className="w-4 h-4" src="/images/icon/eraser.png" alt="" /><span className="text-stone-500 text-sm">Limpar</span></button>
                                            <button type="submit" className="px-4 py-2 w-full bg-stone-600 rounded-md flex items-center justify-center gap-2 disabled:opacity-60" disabled={isLoading}><img className="w-4 h-4" src="/images/icon/filter-white.png" alt="" /><span className="text-white text-sm">{isLoading ? 'Filtrando...' : 'Filtrar'}</span></button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-4">
                            <button type="button" className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-60" disabled={!!exportando || isLoading || totalRecords === 0} onClick={() => exportarDados('excel')}>
                                <img className="w-4 h-4" src="/images/icon/upload.png" alt="" />
                                <span className="text-white text-sm font-medium font-['Inter'] leading-normal">{exportando === 'excel' ? 'Gerando...' : 'Excel'}</span>
                            </button>
                            <button type="button" className="flex px-4 py-2 bg-slate-600 rounded-md border border-stone-600 justify-center items-center gap-2 disabled:opacity-60" disabled={!!exportando || isLoading || totalRecords === 0} onClick={() => exportarDados('pdf')}>
                                <img className="w-4 h-4" src="/images/icon/pdf.png" alt="" />
                                <span className="text-white text-sm font-medium font-['Inter'] leading-normal">{exportando === 'pdf' ? 'Gerando...' : 'PDF'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white border border-slate-200 rounded-md p-4">
                        <div className="text-xs text-slate-500">Boletos no filtro</div>
                        <div className="text-lg font-semibold text-slate-800" data-testid="resumo-quantidade">{resumo.quantidade}</div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-md p-4">
                        <div className="text-xs text-slate-500">Valor total no filtro</div>
                        <div className="text-lg font-semibold text-slate-800" data-testid="resumo-valor">{formatCurrencyFromDb(resumo.valorTotal) || 'R$ 0,00'}</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-300 rounded-md p-4">
                        <div className="text-xs text-amber-900">Sem obra vinculada</div>
                        <div className="text-lg font-semibold text-amber-900" data-testid="resumo-sem-obra">{formatCurrencyFromDb(resumo.semObraValor) || 'R$ 0,00'}</div>
                        <div className="text-xs text-amber-900 mt-1">{resumo.semObraQuantidade} boleto(s) sem nota com obra</div>
                    </div>
                </div>

                {isLoading && (
                    <div className="flex justify-center items-center py-2" role="status" aria-live="polite">
                        <div className="text-stone-600 text-sm">Carregando...</div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className={`min-w-full bg-white border-collapse border border-gray-200 ${isLoading ? 'opacity-60' : ''}`}>
                        <thead className="bg-stone-200">
                            <tr>
                                {visiveis.map((col) => (<th key={col.id} className={`px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold ${col.monetario ? 'text-right' : 'text-left'}`}>{col.label}</th>))}
                                {podeVincular && <th className="sticky right-0 z-10 bg-stone-200 px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-left shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.25)]">Nota</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {lista.length > 0 ? (
                                lista.map((row) => (
                                    <tr key={row.id} className="hover:bg-gray-50">
                                        {visiveis.map(col => (
                                            <td key={`${col.id}-${row.id}`} className={`px-4 py-2 border border-slate-200 text-sm ${col.monetario ? 'text-right whitespace-nowrap' : ''} ${col.data || col.id === 'beneficiarioDocumento' ? 'whitespace-nowrap' : ''} ${col.id === 'obra' ? 'min-w-[13rem]' : ''} ${col.id === 'obra' && !row.obra ? 'text-slate-400 italic' : 'text-slate-700'}`}>
                                                {col.formatter(row[col.id], row)}
                                            </td>
                                        ))}
                                        {podeVincular && (
                                            <td className="sticky right-0 bg-white px-3 py-2 border border-slate-200 text-sm whitespace-nowrap shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.25)]">
                                                {!row.obra ? (
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 rounded-md border border-stone-500 text-stone-700 hover:bg-stone-100 text-sm"
                                                        title={row.notaFiscal ? `Nota ${row.notaFiscal.numero} vinculada, mas sem obra` : 'Escolher a nota fiscal deste boleto'}
                                                        onClick={() => setBoletoVinculo(row)}
                                                    >
                                                        {row.notaFiscal ? 'Trocar nota' : 'Vincular nota'}
                                                    </button>
                                                ) : row.vinculoManual && (
                                                    // Vínculo feito por alguém: dá para corrigir sem sair do relatório
                                                    <button
                                                        type="button"
                                                        className="text-sm text-stone-600 underline hover:text-stone-900"
                                                        title={`Nota ${row.notaFiscal?.numero || ''} escolhida manualmente`}
                                                        onClick={() => setBoletoVinculo(row)}
                                                    >
                                                        Trocar nota
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : !isLoading ? (
                                <tr><td colSpan={(visiveis.length || 1) + (podeVincular ? 1 : 0)} className="text-center py-10 text-slate-500">Nenhum boleto encontrado para os filtros informados.</td></tr>
                            ) : null}
                        </tbody>
                        {lista.length > 0 && indiceValorTabela >= 0 && (
                            <tfoot>
                                <tr className="bg-stone-100 font-semibold">
                                    {indiceValorTabela > 0 && (
                                        <td className="px-4 py-2 border border-slate-200 text-right text-sm" colSpan={indiceValorTabela}>Total da página</td>
                                    )}
                                    <td className="px-4 py-2 border border-slate-200 text-right text-sm whitespace-nowrap" title="Total da página">{formatCurrencyFromDb(totalPagina)}</td>
                                    {visiveis.length - indiceValorTabela - 1 + (podeVincular ? 1 : 0) > 0 && (
                                        <td className="px-4 py-2 border border-slate-200" colSpan={visiveis.length - indiceValorTabela - 1 + (podeVincular ? 1 : 0)}></td>
                                    )}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {totalRecords > 0 && (
                    <div className="self-stretch flex flex-wrap justify-end items-center gap-2.5">
                        <div className="grow shrink basis-0 text-slate-500 text-base font-normal">Mostrando de {startRecord} até {endRecord} de {totalRecords} registros.</div>
                        <div className="justify-start items-start gap-4 flex">
                            <button type="button" aria-label="Página anterior" className="w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center" disabled={currentPage === 1 || isLoading} onClick={() => handlePageChange(currentPage - 1)}><img className="w-6 h-6" src="/images/icon/keyboard-arrow-left.png" alt="" /></button>
                            {pageNumbers.map((page) => (<button type="button" key={page} aria-current={page === currentPage ? 'page' : undefined} className={`w-10 h-10 rounded flex justify-center items-center font-semibold ${page === currentPage ? "bg-stone-600 text-white" : "bg-gray-200 text-gray-600"}`} disabled={isLoading} onClick={() => handlePageChange(page)}>{page}</button>))}
                            <button type="button" aria-label="Próxima página" className="w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center" disabled={currentPage === totalPages || isLoading} onClick={() => handlePageChange(currentPage + 1)}><img className="w-6 h-6" src="/images/icon/keyboard-arrow-right.png" alt="" /></button>
                        </div>
                    </div>
                )}
                {boletoVinculo && (
                    <ModalVincularNotaBoleto
                        boleto={boletoVinculo}
                        onFechar={() => setBoletoVinculo(null)}
                        onVinculado={(nota) => {
                            setBoletoVinculo(null);
                            // Recarrega a página atual: a obra entra na linha e os cartões mudam
                            fetchBoletos(currentPage, pageSize, appliedFiltersRef.current);
                            setMessage(nota.obra
                                ? `Nota ${nota.numero} vinculada. O boleto passou para a obra ${nota.obra.nomeProjeto.toUpperCase()}.`
                                : `Nota ${nota.numero} vinculada. Essa nota não tem obra cadastrada, então o boleto continua sem obra.`);
                            setShowAlertDialog(true);
                        }}
                        onDesvinculado={(nota) => {
                            setBoletoVinculo(null);
                            fetchBoletos(currentPage, pageSize, appliedFiltersRef.current);
                            setMessage(`Vínculo com a nota ${nota?.numero || ''} removido. O boleto voltou a ficar sem obra.`);
                            setShowAlertDialog(true);
                        }}
                    />
                )}
                <AlertDialog isVisible={showAlertDialog} message={message} onClose={() => setShowAlertDialog(false)} />
            </div>
        </Template>
    );
};

export default RelatorioBoletos;
